import { useState } from 'react';

import { Typography } from '@mui/material';
import axios from 'axios';
import { useTranslation } from 'react-i18next';

import { getRecordingName } from './util';
import { RECORDING_DELETE } from '../../config/apiPaths';
import { useSchoolInfo } from '../../hooks/useSchoolInfo';
import { RecordingStatus } from '../../types/recordingTypes';
import { processApiPath } from '../../utils/url';
import { type RecordingDialogProps } from '../classroom/ui-types';
import { DeleteConfirmationDialog } from '../common/dialog/DeleteConfirmationDialog';

export const DeleteRecordingDialog = (props: RecordingDialogProps) => {
    const { t } = useTranslation();
    const { open, recording, recordings, setRecordings, onClose, classUuid, userUuid } = props;
    const { data: schoolInfo } = useSchoolInfo();
    const schoolUuid = schoolInfo?.uuid;
    const [confirming, setConfirming] = useState(false);

    const handleClose = () => {
        onClose?.({}, 'backdropClick');
    };

    const handleDialogConfirmed = async () => {
        if (!schoolUuid || !classUuid || !recording?.recordingId) {
            console.error('Missing required data to delete recording');
            handleClose();
            return;
        }

        const doDelete = async () => {
            const url = processApiPath(
                RECORDING_DELETE,
                {
                    schoolUuid,
                    classUuid,
                    recordingId: recording.recordingId
                },
                { action: 'SOFT' }
            );
            setConfirming(true);
            try {
                await axios.delete(url, {
                    headers: {
                        user_uuid: userUuid
                    }
                });
            } catch (e) {
                console.error(e);
                // TODO show error here.
            } finally {
                setConfirming(false);
            }
        };

        await doDelete();

        // Mark the recording deleted.
        const newRecordings = recordings.map((r) => {
            if (r.recordingId === recording?.recordingId) {
                r.status = RecordingStatus.DELETED;
            }
            return r;
        });

        setRecordings(newRecordings);
        handleClose();
    };

    return (
        <DeleteConfirmationDialog
            id="delete-recording"
            open={open}
            onClose={handleClose}
            title={t('lti:recordings.delete-title')}
            confirmLabel={t('common:delete')}
            onConfirm={handleDialogConfirmed}
            confirming={confirming}
            setConfirming={setConfirming}
        >
            <Typography>
                {t('lti:recordings.delete-confirmation', {
                    name: getRecordingName(t, recording),
                    interpolation: { escapeValue: false } // Already escaped.
                })}
            </Typography>
        </DeleteConfirmationDialog>
    );
};
